import { useEffect, useMemo, useRef } from 'react'
import { RegisterOptions } from 'react-hook-form'
import {
  Box,
  FormControl,
  InputGroup,
  InputRightElement,
  useMergeRefs,
} from '@chakra-ui/react'
import { extend, pick } from 'lodash'

import { EmailFieldBase } from '~shared/types/field'
import { FormResponseMode } from '~shared/types/form'
import { validateEmailDomains } from '~shared/utils/email-domain-validation'

import { GUIDE_PREFILL } from '~constants/links'
import { createBaseValidationRules } from '~utils/fieldValidation'
import FormErrorMessage from '~components/FormControl/FormErrorMessage'
import FormLabel from '~components/FormControl/FormLabel'
import Input from '~components/Input'
import Textarea from '~components/Textarea'
import Toggle from '~components/Toggle'
import { CopyButton } from '~templates/CopyButton'

import { CreatePageDrawerContentContainer } from '../../../../../common'
import { useCreateTabForm } from '../../../../useCreateTabForm'
import { SPLIT_TEXTAREA_TRANSFORM } from '../common/constants'
import { FormFieldDrawerActions } from '../common/FormFieldDrawerActions'
import { EditFieldProps } from '../common/types'
import { useEditFieldForm } from '../common/useEditFieldForm'

const EDIT_EMAIL_FIELD_KEYS = [
  'title',
  'description',
  'required',
  'isVerifiable',
  'autoReplyOptions',
  'allowPrefill',
  'lockPrefill',
] as const

type EditEmailProps = EditFieldProps<EmailFieldBase>

type EditEmailInputs = Pick<
  EmailFieldBase,
  typeof EDIT_EMAIL_FIELD_KEYS[number]
> & {
  hasAllowedEmailDomains: boolean
  allowedEmailDomains: string
}

const transformEmailFieldToEditForm = (
  field: EmailFieldBase,
): EditEmailInputs => {
  const allowedEmailDomains = field.allowedEmailDomains
  return {
    ...pick(field, EDIT_EMAIL_FIELD_KEYS),
    hasAllowedEmailDomains: allowedEmailDomains.length > 0,
    allowedEmailDomains: SPLIT_TEXTAREA_TRANSFORM.input(allowedEmailDomains),
  }
}

const transformEmailEditFormToField = (
  inputs: EditEmailInputs,
  originalField: EmailFieldBase,
): EmailFieldBase => {
  const { allowedEmailDomains, hasAllowedEmailDomains, ...rest } = inputs
  return extend({}, originalField, rest, {
    hasAllowedEmailDomains,
    // Clear allowedEmailDomains when toggled off.
    allowedEmailDomains: hasAllowedEmailDomains
      ? SPLIT_TEXTAREA_TRANSFORM.output(allowedEmailDomains)
      : [],
  })
}

export const EditEmail = ({ field }: EditEmailProps): JSX.Element => {
  const {
    register,
    formState: { errors },
    buttonText,
    handleUpdateField,
    watch,
    isLoading,
    handleCancel,
  } = useEditFieldForm<EditEmailInputs, EmailFieldBase>({
    field,
    transform: {
      input: transformEmailFieldToEditForm,
      output: transformEmailEditFormToField,
    },
  })

  const watchedHasAllowedEmailDomains = watch('hasAllowedEmailDomains')
  const watchedHasAutoReply = watch('autoReplyOptions.hasAutoReply')

  const requiredValidationRule = useMemo(
    () => createBaseValidationRules({ required: true }),
    [],
  )

  const allowedEmailDomainsRegister = useMemo(
    () => register('hasAllowedEmailDomains'),
    [register],
  )
  const hasAllowedEmailDomainsRef = useRef<HTMLInputElement>(null)
  const mergedAllowedEmailDomainsRef = useMergeRefs(
    hasAllowedEmailDomainsRef,
    allowedEmailDomainsRegister.ref,
  )

  const emailDomainsValidation = useMemo<
    RegisterOptions<EditEmailInputs, 'allowedEmailDomains'>
  >(
    () => ({
      ...requiredValidationRule,
      validate: {
        noDuplicate: (value) => {
          const split = SPLIT_TEXTAREA_TRANSFORM.output(value)
          return (
            new Set(split).size === split.length ||
            'Please remove duplicate email domains'
          )
        },
        noEmpty: (value) => {
          const split = SPLIT_TEXTAREA_TRANSFORM.output(value)
          return split.length > 0 || 'Please enter at least one email domain'
        },
        validEmailDomains: (value) => {
          const split = SPLIT_TEXTAREA_TRANSFORM.output(value)
          return (
            validateEmailDomains(split) ||
            'Please enter only valid email domains starting with @'
          )
        },
      },
    }),
    [requiredValidationRule],
  )

  const { data: form } = useCreateTabForm()
  const isPdfResponseEnabled = useMemo(
    () => form?.responseMode !== FormResponseMode.Encrypt,
    [form],
  )
  const pdfResponseToggleDescription = useMemo(() => {
    if (!isPdfResponseEnabled) {
      return 'For security reasons, PDF responses are not included in email confirmations for Storage mode forms'
    }
  }, [isPdfResponseEnabled])

  const hasLockPrefillRef = useRef<HTMLInputElement>(null)
  const hasEmailDomainsRef = useRef<HTMLInputElement>(null)

  const allowPrefillRegister = useMemo(
    () => register('allowPrefill'),
    [register],
  )
  const lockPrefillRegister = useMemo(() => register('lockPrefill'), [register])

  const mergedAllowPrefillRef = useMergeRefs(
    hasEmailDomainsRef,
    allowPrefillRegister.ref,
  )

  const mergedLockPrefillRef = useMergeRefs(
    hasLockPrefillRef,
    lockPrefillRegister.ref,
  )

  const watchAllowPrefill = watch('allowPrefill')
  const watchLockPrefill = watch('lockPrefill')
  const watchLockEmailDomains = !watch('allowedEmailDomains')

  useEffect(() => {
    // Prefill must be enabled for lockPrefill
    // We cannot simply use setValue as it does not update
    // the UI
    if (!watchAllowPrefill && watchLockPrefill) {
      hasLockPrefillRef.current?.click()
    }

    if (!watchLockEmailDomains) {
      hasEmailDomainsRef.current?.click()
    }
  }, [watchAllowPrefill, watchLockPrefill, watchLockEmailDomains])

  return (
    <CreatePageDrawerContentContainer>
      <FormControl isRequired isReadOnly={isLoading} isInvalid={!!errors.title}>
        <FormLabel>Question</FormLabel>
        <Input autoFocus {...register('title', requiredValidationRule)} />
        <FormErrorMessage>{errors?.title?.message}</FormErrorMessage>
      </FormControl>
      <FormControl
        isRequired
        isReadOnly={isLoading}
        isInvalid={!!errors.description}
      >
        <FormLabel>Description</FormLabel>
        <Textarea {...register('description')} />
        <FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isReadOnly={isLoading}>
        <Toggle {...register('required')} label="Required" />
      </FormControl>
      <FormControl isReadOnly={isLoading}>
        <Toggle
          {...register('isVerifiable')}
          label="OTP verification"
          description="Respondents must verify by entering a code sent to them"
        />
      </FormControl>
      <Box>
        <FormControl isReadOnly={isLoading}>
          <Toggle
            {...allowedEmailDomainsRegister}
            ref={mergedAllowedEmailDomainsRef}
            label="Restrict email domains"
          />
        </FormControl>
        {watchedHasAllowedEmailDomains && (
          <FormControl
            isReadOnly={isLoading}
            isRequired
            isInvalid={!!errors.allowedEmailDomains}
            mt="1.5rem"
          >
            <FormLabel>Domains allowed</FormLabel>
            <Textarea
              autoFocus
              {...register('allowedEmailDomains', emailDomainsValidation)}
              placeholder={'@data.gov.sg\n@agency.gov.sg'}
            />
            <FormErrorMessage>
              {errors?.allowedEmailDomains?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      </Box>
      <Box>
        <FormControl isReadOnly={isLoading}>
          <Toggle
            {...register('autoReplyOptions.hasAutoReply')}
            description="Customise an email acknowledgement to respondents"
            label="Email confirmation"
          />
        </FormControl>
        {watchedHasAutoReply && (
          <>
            <FormControl isRequired isReadOnly={isLoading} mt="1.5rem">
              <FormLabel>Subject</FormLabel>
              <Input
                autoFocus
                placeholder="Default email subject"
                {...register('autoReplyOptions.autoReplySubject')}
              />
            </FormControl>
            <FormControl isRequired isReadOnly={isLoading} mt="1.5rem">
              <FormLabel>Sender name</FormLabel>
              <Input
                placeholder="Default sender name is your agency name"
                {...register('autoReplyOptions.autoReplySender')}
              />
            </FormControl>
            <FormControl isReadOnly={isLoading} isRequired mt="1.5rem">
              <FormLabel>Content</FormLabel>
              <Textarea
                placeholder="Default email body"
                {...register('autoReplyOptions.autoReplyMessage')}
              />
            </FormControl>
            <FormControl isReadOnly={isLoading} mt="1.5rem">
              <Toggle
                {...register('autoReplyOptions.includeFormSummary')}
                label="Include PDF response"
                description={pdfResponseToggleDescription}
                isDisabled={!isPdfResponseEnabled}
              />
            </FormControl>
          </>
        )}
      </Box>
      <FormControl isReadOnly={isLoading}>
        <Toggle
          {...allowPrefillRegister}
          label="Enable pre-fill"
          ref={mergedAllowPrefillRef}
          description={`Use Field ID in the form URL to pre-fill this field for respondents. [Learn how](${GUIDE_PREFILL})`}
          isDisabled={!watchLockEmailDomains}
        />
        {watchAllowPrefill ? (
          <>
            <InputGroup mt="0.5rem">
              <Input
                isReadOnly
                isDisabled={!field._id}
                value={
                  field._id ??
                  'Field ID will be generated after this field is saved'
                }
              />
              {field._id ? (
                <InputRightElement>
                  <CopyButton
                    stringToCopy={field._id}
                    aria-label="Copy field ID value"
                  />
                </InputRightElement>
              ) : null}
            </InputGroup>
          </>
        ) : null}
      </FormControl>
      <FormControl isReadOnly={isLoading}>
        <Toggle
          {...lockPrefillRegister}
          ref={mergedLockPrefillRef}
          label="Prevent pre-fill editing"
          description="This prevents respondents from clicking the field to edit it. However, field content can still be modified via the URL."
          isDisabled={!watchAllowPrefill || !watchLockEmailDomains}
        />
      </FormControl>
      <FormFieldDrawerActions
        isLoading={isLoading}
        buttonText={buttonText}
        handleClick={handleUpdateField}
        handleCancel={handleCancel}
      />
    </CreatePageDrawerContentContainer>
  )
}
